/**
 * Synthetic task rows derived from project member responsibilities.
 * Surfaces assignee work in the Tasks module until `/api/pm/tasks` links them server-side.
 */

import {
  projectId,
  normalizeMembers,
  hasValidMemberResponsibility,
  getMemberResponsibilityTitle,
  getMemberResponsibilityDescription,
  getResponsibilityGroupKey,
  getProjectResponsibilities,
  roleLabel,
} from './project-config'
import { TASK_STATUSES, normalizeTaskStatus } from './task-config'

export const PROJECT_RESPONSIBILITY_SOURCE = 'project-responsibility'

/** Stable synthetic id — one task row per responsibility assignee. */
export function projectResponsibilityTaskId(projectIdValue, memberEmpId, responsibilityId = null) {
  if (responsibilityId) return `proj-resp-${projectIdValue}-${responsibilityId}-${memberEmpId}`
  return `proj-resp-${projectIdValue}-${memberEmpId}`
}

export function isProjectResponsibilityTask(task) {
  return task?.source === PROJECT_RESPONSIBILITY_SOURCE
}

/**
 * Build one synthetic task per responsibility assignee.
 */
export function buildProjectResponsibilityTasks(projects) {
  const list = Array.isArray(projects) ? projects : []
  const tasks = []

  for (const project of list) {
    const pid = projectId(project)
    if (pid == null) continue
    const members = normalizeMembers(project.members)
    const memberById = new Map(members.map(m => [String(m.empId), m]))
    const responsibilities = getProjectResponsibilities(project)

    if (responsibilities.length) {
      for (const resp of responsibilities) {
        for (const assignee of resp.assignees || []) {
          const member = memberById.get(String(assignee.empId))
          if (!member) continue
          tasks.push(buildTaskFromResponsibility(project, member, resp, assignee))
        }
      }
      continue
    }

    // Legacy fallback — one task per member note.
    for (const member of members) {
      if (!hasValidMemberResponsibility(member)) continue
      tasks.push(buildTaskFromMember(project, member))
    }
  }

  return tasks
}

function buildTaskFromResponsibility(project, member, resp, assignee) {
  const pid = projectId(project)
  const empId = String(member.empId)
  const done = !!assignee.completed
  const creator = project.createdBy ?? project.created_by

  return {
    id: projectResponsibilityTaskId(pid, empId, resp.id),
    title: resp.responsibilityTitle || getMemberResponsibilityTitle(member),
    description: resp.responsibilityDescription || getMemberResponsibilityDescription(member),
    status: done ? TASK_STATUSES.DONE : TASK_STATUSES.TODO,
    priority: 'Medium',
    assignedTo: empId,
    assignedToName: member.name || empId,
    createdBy: creator != null ? String(creator) : empId,
    createdByName: project.createdByName || project.created_by_name || 'Project',
    projectId: pid,
    projectName: project.name || 'Project',
    projectMemberEmpId: empId,
    responsibilityGroupId: resp.id || null,
    source: PROJECT_RESPONSIBILITY_SOURCE,
    isSynthetic: true,
    isDeleted: false,
    dueDate: null,
    completedAt: assignee.completedAt || null,
    order: resp.responsibilityOrder || 0,
    attachments: resp.attachments || [],
  }
}

function buildTaskFromMember(project, member) {
  const pid = projectId(project)
  const empId = String(member.empId)
  const done = !!member.responsibilityCompleted
  const creator = project.createdBy ?? project.created_by

  return {
    id: projectResponsibilityTaskId(pid, empId),
    title: getMemberResponsibilityTitle(member),
    description: getMemberResponsibilityDescription(member),
    status: done ? TASK_STATUSES.DONE : TASK_STATUSES.TODO,
    priority: 'Medium',
    assignedTo: empId,
    assignedToName: member.name || empId,
    createdBy: creator != null ? String(creator) : empId,
    createdByName: project.createdByName || project.created_by_name || 'Project',
    projectId: pid,
    projectName: project.name || 'Project',
    projectMemberEmpId: empId,
    responsibilityGroupId: member.responsibilityGroupId || null,
    source: PROJECT_RESPONSIBILITY_SOURCE,
    isSynthetic: true,
    isDeleted: false,
    dueDate: null,
    completedAt: member.responsibilityCompletedAt || null,
    order: 0,
    attachments: member.attachments || [],
  }
}

/**
 * Replace prior synthetic rows with a fresh build from current project data.
 */
export function mergeProjectResponsibilityTasks(regularTasks, projects) {
  const base = (regularTasks || []).filter(t => !isProjectResponsibilityTask(t))
  return [...base, ...buildProjectResponsibilityTasks(projects)]
}

/** Open (incomplete) project responsibilities assigned to `empId`. */
export function countOpenProjectResponsibilities(projects, empId) {
  if (empId == null) return 0
  const id = String(empId)
  let count = 0
  for (const project of projects || []) {
    const responsibilities = getProjectResponsibilities(project)
    if (responsibilities.length) {
      for (const resp of responsibilities) {
        for (const assignee of resp.assignees || []) {
          if (String(assignee.empId) === id && !assignee.completed) count += 1
        }
      }
      continue
    }
    for (const member of normalizeMembers(project.members)) {
      if (String(member.empId) !== id) continue
      if (!hasValidMemberResponsibility(member)) continue
      if (!member.responsibilityCompleted) count += 1
    }
  }
  return count
}

/** Whether an open synthetic task should bypass due-date board filters. */
export function isOpenProjectResponsibilityTask(task) {
  if (!isProjectResponsibilityTask(task)) return false
  return normalizeTaskStatus(task) !== TASK_STATUSES.DONE
}

/**
 * Group numbered responsibility steps by shared group id or normalized text.
 * Returns one row per unique responsibility/work item.
 */
export function buildResponsibilityStepsGrouped(steps) {
  if (!Array.isArray(steps) || !steps.length) return []

  const groups = new Map()
  for (const step of steps) {
    const key = getResponsibilityGroupKey(step) || `solo:${step.empId}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(step)
  }

  const grouped = Array.from(groups.entries()).map(([groupKey, members]) => {
    members.sort((a, b) => a.stepNumber - b.stepNumber)
    const primary = members[0]
    const roles = [...new Set(members.map(m => m.role))]
    const rolePart = roles.length === 1
      ? roleLabel(roles[0])
      : roles.map(r => roleLabel(r)).join(' / ')
    const assigneeNames = members.map(m => m.name || m.empId).join(', ')

    return {
      groupKey,
      stepNumber: primary.stepNumber,
      responsibilityTitle: getMemberResponsibilityTitle(primary),
      responsibilityDescription: getMemberResponsibilityDescription(primary),
      attachments: primary.attachments || [],
      members,
      primaryMember: primary,
      isShared: members.length > 1,
      allDone: members.every(m => m.responsibilityCompleted),
      roleSummary: rolePart,
      assigneeNames,
      completionEntries: members.map(m => ({
        empId: String(m.empId),
        name: m.name || m.empId,
        member: m,
        completed: !!m.responsibilityCompleted,
      })),
    }
  })

  grouped.sort((a, b) => a.stepNumber - b.stepNumber)
  return grouped.map((g, index) => ({ ...g, stepNumber: index + 1 }))
}
